/* SpawnAnything — formations & orders (client side): a formation picker per army in the setup panel,
   the order bar in the battle HUD and the rally-point markers. The sim side lives in src/sim/features/formation.js.
   Formation travels in the worker `setup` message ({ formation: [k0, k1] }); orders are commands
   ({ cmd: 'formationOrder', team, o, x?, z?, s? } -> cmdQ), so lockstep can send them as { t: 'formationOrder', … }. */
import * as THREE from 'three';
import './formation.css';
import { FM_PINCER, ORD_ADVANCE, ORD_HOLD, ORD_RALLY, ORD_FLANK } from '../sim/features/formation.js';

const FORMATIONS = [
  { name: 'Block', desc: 'Breiter Block wie bisher.' },
  { name: 'Linie', desc: 'Breite, flache Front: viele kämpfen gleichzeitig.' },
  { name: 'Keil', desc: 'Spitze voran: bricht in die feindliche Mitte.' },
  { name: 'Schildkröte', desc: 'Enger Block: 30 % langsamer, nimmt nur halben Fernkampfschaden.' },
  { name: 'Zange', desc: 'Zwei Flügel mit Lücke in der Mitte. Passt gut zum Befehl Flanke.' },
];
const ORDERS = [
  { name: 'Vorrücken', title: 'Auf den nächsten Feind losgehen (Standard)', said: 'rückt vor' },
  { name: 'Halten', title: 'Stellung halten, nur in Reichweite kämpfen', said: 'hält die Stellung' },
  { name: 'Sammelpunkt', title: 'Danach aufs Feld klicken: die Armee läuft dorthin und kämpft unterwegs', said: 'läuft zum Sammelpunkt' },
  { name: 'Flanke', title: 'Um die Seite des Feindes herum angreifen. Nochmal klicken wechselt die Seite.', said: 'greift die Flanke an' },
];
const SIDES = { 1: 'rechts', '-1': 'links', 0: 'beide Seiten' };
const NEXT_SIDE = { 1: -1, '-1': 0, 0: 1 };

/** opts: { command(c) -> sent?, scene, teams, canvas, groundAt(cx, cy, out), groundH(x, z), getState(), isPossessed(), onChange(), toast(msg),
    ownTeam() -> team the player may order (-1 = both, single player), locked() -> no orders (betting) } */
export function createFormation({ command, scene, teams, canvas, groundAt, groundH, getState, isPossessed, onChange, toast, ownTeam, locked, mixed }) {
  const $ = s => document.querySelector(s);
  const ord = [ORD_ADVANCE, ORD_ADVANCE], side = [1, 1], rally = [null, null];
  let team = 0, armed = false, down = null, shown = null, low = null, lastOwn = -2;
  const hit = new THREE.Vector3();

  /* ---- setup panel: formation per army ---- */
  const sel = [0, 1].map(t => {
    const stats = $('#stats' + t);
    const lab = document.createElement('label'); lab.htmlFor = 'formation' + t; lab.textContent = 'Formation';
    const s = document.createElement('select'); s.id = 'formation' + t;
    s.innerHTML = FORMATIONS.map((f, k) => `<option value="${k}">${f.name}</option>`).join('');
    const desc = document.createElement('p'); desc.className = 'formation-desc';
    stats.before(lab, s, desc);
    const sync = () => {
      // a mixed side lines up by role (close combat in front, ranged behind): block / wedge / turtle … do not apply there
      const mx = !!(mixed && mixed(t)); s.disabled = mx;
      desc.textContent = mx ? 'Gemischte Armee: stellt sich nach Rolle auf (Nahkampf vorn, Schützen dahinter, Riesen außen).' : FORMATIONS[+s.value].desc;
    };
    s.addEventListener('change', () => { sync(); onChange(); });
    sync(); s._sync = sync;
    return s;
  });
  const kindOf = t => (mixed && mixed(t) ? 0 : +sel[t].value | 0);

  /* ---- battle HUD: order bar ---- */
  const bar = document.createElement('div');
  bar.className = 'formation-bar'; bar.hidden = true;
  bar.setAttribute('role', 'toolbar'); bar.setAttribute('aria-label', 'Befehle');
  bar.innerHTML = `<span class="formation-th">Befehle</span>
    <span class="formation-team"><button class="formation-t0" aria-pressed="true" title="Befehle für Blau">Blau</button><button class="formation-t1" aria-pressed="false" title="Befehle für Rot">Rot</button></span>
    ${ORDERS.map((o, k) => `<button data-formation-order="${k}" title="${o.title}" aria-pressed="false">${o.name}${k === ORD_FLANK ? ' <small></small>' : ''}</button>`).join('')}`;
  document.body.appendChild(bar);
  const teamBtn = [bar.querySelector('.formation-t0'), bar.querySelector('.formation-t1')];
  const ordBtn = [...bar.querySelectorAll('[data-formation-order]')];
  const sideTxt = ordBtn[ORD_FLANK].querySelector('small');
  teamBtn.forEach((b, t) => b.addEventListener('click', () => { team = t; disarm(); syncBar(); }));
  ordBtn.forEach((b, k) => b.addEventListener('click', () => pick(k)));

  function syncBar() {
    teamBtn.forEach((b, t) => b.setAttribute('aria-pressed', String(t === team)));
    ordBtn.forEach((b, k) => {
      b.setAttribute('aria-pressed', String(k === ORD_RALLY ? armed || ord[team] === k : ord[team] === k));
      b.classList.toggle('formation-arm', k === ORD_RALLY && armed);
    });
    sideTxt.textContent = ord[team] === ORD_FLANK ? SIDES[side[team]] : '';
  }
  // order command { t: 'fo', team, o, x?, z?, s? } (rally point quantized to 1/16 m, like every lockstep position)
  function send(t, o, extra) {
    const c = { t: 'fo', team: t, o };
    if (extra && extra.x !== undefined) { c.x = Math.round(extra.x * 16); c.z = Math.round(extra.z * 16); }
    if (extra && extra.s !== undefined) c.s = extra.s;
    if (!command(c)) return;
    ord[t] = o;
    toast(`${teams[t].name} ${ORDERS[o].said}${o === ORD_FLANK ? ' (' + SIDES[side[t]] + ')' : ''}`);
    syncBar();
  }
  function pick(k) {
    if (k === ORD_RALLY) { armed = !armed; if (armed) toast('Klicke aufs Feld, um den Sammelpunkt zu setzen'); syncBar(); return; }
    disarm();
    if (k === ORD_FLANK) side[team] = ord[team] === ORD_FLANK ? NEXT_SIDE[side[team]] : kindOf(team) === FM_PINCER ? 0 : 1;
    send(team, k, k === ORD_FLANK ? { s: side[team] } : null);
  }
  function disarm() { if (!armed) return; armed = false; ghost.visible = false; syncBar(); }

  /* ---- rally markers: flag + pulsing ring in the team colour; a white one follows the cursor while placing ---- */
  const ringGeo = new THREE.RingGeometry(1.5, 1.85, 40); ringGeo.rotateX(-Math.PI / 2);
  const poleGeo = new THREE.CylinderGeometry(0.07, 0.07, 3.4, 6); poleGeo.translate(0, 1.7, 0);
  const flagGeo = new THREE.BufferGeometry();
  flagGeo.setAttribute('position', new THREE.Float32BufferAttribute([0, 3.35, 0, 0, 2.35, 0, 1.4, 2.85, 0], 3));
  function marker(color) {
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, depthWrite: false, side: THREE.DoubleSide });
    const g = new THREE.Group(), ring = new THREE.Mesh(ringGeo, mat), pole = new THREE.Mesh(poleGeo, mat), flag = new THREE.Mesh(flagGeo, mat);
    ring.position.y = 0.08; g.add(ring, pole, flag); g.visible = false; g.renderOrder = 2; g.userData.ring = ring;
    scene.add(g);
    return g;
  }
  const marks = [marker(teams[0].light), marker(teams[1].light)], ghost = marker(0xffffff);
  ghost.userData.ring.material.opacity = 0.6;

  function placeRally(cx, cy) {
    if (!groundAt(cx, cy, hit)) return;
    const t = team;
    armed = false; ghost.visible = false;
    send(t, ORD_RALLY, { x: hit.x, z: hit.z });
    if (ord[t] === ORD_RALLY) rally[t] = { x: hit.x, z: hit.z };
  }
  // while placing, a left click on the field belongs to the rally point (capture phase: before tools / possession)
  addEventListener('pointerdown', e => {
    if (!armed || e.target !== canvas || e.button !== 0 || down || isPossessed() || getState() === 'setup') return;
    e.stopPropagation();
    down = { id: e.pointerId, x: e.clientX, y: e.clientY };
  }, true);
  addEventListener('pointerup', e => {
    if (!down || e.pointerId !== down.id) return;
    e.stopPropagation();
    const near = Math.hypot(e.clientX - down.x, e.clientY - down.y) < 10;
    down = null;
    if (near) placeRally(e.clientX, e.clientY);
  }, true);
  addEventListener('pointercancel', e => { if (down && e.pointerId === down.id) down = null; }, true);
  addEventListener('pointermove', e => {
    if (!armed || e.target !== canvas || e.pointerType === 'touch' || !groundAt(e.clientX, e.clientY, hit)) { ghost.visible = false; return; }
    ghost.visible = true; ghost.position.set(hit.x, groundH(hit.x, hit.z), hit.z);
  });
  addEventListener('keydown', e => { if (e.code === 'Escape' && armed) { disarm(); e.preventDefault(); } }, true); // Esc only disarms (main.js: no pause menu)

  return {
    /** extra fields for the worker `setup` message */
    setupMsg: () => ({ formation: [kindOf(0), kindOf(1)] }),
    /** troops changed: a mixed side cannot pick a formation */
    refresh: () => sel.forEach(x => x._sync()),
    /** new battle: the worker starts every army with "advance" */
    reset() {
      ord[0] = ord[1] = ORD_ADVANCE; rally[0] = rally[1] = null; armed = false; down = null;
      ghost.visible = marks[0].visible = marks[1].visible = false;
      syncBar();
    },
    frame(now) {
      const st = getState(), battle = st === 'running' && !locked(); // no orders in the setup, in replays and while betting
      const own = ownTeam();
      if (own !== lastOwn) { lastOwn = own; teamBtn.forEach((b, t) => { b.hidden = own >= 0 && t !== own; }); } // multiplayer: only the own army
      if (own >= 0 && team !== own) { team = own; disarm(); syncBar(); }
      if (shown !== battle) { shown = battle; bar.hidden = !battle; if (!battle) disarm(); }
      if (!battle) { marks[0].visible = marks[1].visible = false; return; }
      const up = isPossessed();
      if (low !== up) { low = up; bar.classList.toggle('formation-low', up); } // playback bar moved to the top
      const k = 1 + 0.12 * Math.sin(now * 0.006);
      for (let t = 0; t < 2; t++) {
        const m = marks[t], r = rally[t];
        m.visible = !!r && ord[t] === ORD_RALLY;
        if (!m.visible) continue;
        m.position.set(r.x, groundH(r.x, r.z), r.z);
        m.userData.ring.scale.setScalar(k);
      }
    },
  };
}
